import {
  getAWSResourceArnFromHybridCatalogId,
  getHybridCatalogResourceArnFromHybridCatalogId,
} from 'src/components/utils/arnUtil';
import {
  getAllMetaDataFieldsForForm,
  getAllMetaDataValuesForFormAndResource,
  listAllMetadataFormEntitiesAssociatedForResource,
} from 'src/commons/commonHCApiCalls';
import {ACCOUNT_DETAIL_GLOBAL_FORM_NAME, COST_CENTER_NAME_METADATA_FIELD_KEY} from 'src/commons/constants';
import {removeSpecialCharactersFromValue} from 'src/components/utils/regexUtil';
import { QueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { isLatticeLineageAvailable } from 'src/api/config';
import { getLatticeLineageForAsset } from 'src/api/latticelineage';

export const getBaseNodeIdV2 = async (datasetId: string): Promise<string | undefined> => {
  if (!datasetId) return undefined;

  try {
    const hybridCatalogDatasetArn = getHybridCatalogResourceArnFromHybridCatalogId(datasetId);
    const awsDatasetArn = getAWSResourceArnFromHybridCatalogId(datasetId);
    
    if (!hybridCatalogDatasetArn || !awsDatasetArn) return undefined;

    const accountDetailsFormId = await getAccountDetailsFormIdAttachedToHCResource(hybridCatalogDatasetArn);
    if (!accountDetailsFormId) return undefined;

    const costCenterFieldId = await getCostCenterFieldIdForForm(accountDetailsFormId);
    if (!costCenterFieldId) return undefined;

    const filteredCostCenterValue = await getCostCenterValueFromGivenFormFieldAndResource(
      accountDetailsFormId,
      costCenterFieldId,
      hybridCatalogDatasetArn,
    );
    if (!filteredCostCenterValue) return undefined;
    
    return filteredCostCenterValue + '#' + awsDatasetArn;
  } catch (err) {
    console.log(`Failed to get base node ID v2 for dataset: ${datasetId}. Reason: ${err}`);
    return undefined;
  }
};

const getAccountDetailsFormIdAttachedToHCResource = async (hcDatasetArn: string): Promise<string | undefined> => {
  try {
    const allFormsAttachedToResource = await listAllMetadataFormEntitiesAssociatedForResource(hcDatasetArn);
    const requiredFormAsList = allFormsAttachedToResource.filter(
      (form) => form?.Name === ACCOUNT_DETAIL_GLOBAL_FORM_NAME,
    );
    return requiredFormAsList.length > 0 ? requiredFormAsList[0].MetadataFormId : undefined;
  } catch (err) {
    console.log(`Failed to fetch metadata forms for resource: ${hcDatasetArn}`);
    return undefined;
  }
};

const getCostCenterFieldIdForForm = async (formId: string): Promise<string | undefined> => {
  try {
    const allFieldsForForm = await getAllMetaDataFieldsForForm(formId);
    const requiredFieldAsList = allFieldsForForm.filter((field) => field?.Name === COST_CENTER_NAME_METADATA_FIELD_KEY);
    return requiredFieldAsList.length > 0 ? requiredFieldAsList[0].MetadataFieldId : undefined;
  } catch (err) {
    console.log(`Failed to fetch metadata fields for form: ${formId}`);
    return undefined;
  }
};

const getCostCenterValueFromGivenFormFieldAndResource = async (
  formId: string,
  fieldId: string,
  hcDatasetArn: string,
): Promise<string | undefined> => {
  try {
    const allValuesForFormAndResource = await getAllMetaDataValuesForFormAndResource(formId, hcDatasetArn);
    const requiredValueAsList = allValuesForFormAndResource.filter((value) => value?.Id === fieldId);
    if (requiredValueAsList.length === 0) return undefined;
    
    return removeSpecialCharactersFromValue(requiredValueAsList[0].Value?.StringValue);
  } catch (err) {
    console.log(
      `Failed to fetch metadata value for form:${formId}, field: ${fieldId}, resource:${hcDatasetArn}. Reason: ${err}`,
    );
    return undefined;
  }
};


export const useLineageAvailable = (lineageBaseNodeId: string | undefined) => {
  const [lineageEnabled, setLineageEnabled] = useState(false);
  const [loadingLineage, setLoadingLineage] = useState(true);

  useEffect(() => {
    if (!isLatticeLineageAvailable() || !lineageBaseNodeId) {
      setLineageEnabled(false);
      setLoadingLineage(false);
      return;
    }
    let cancelled = false;
    setLoadingLineage(true);
    getLatticeLineageForAsset(lineageBaseNodeId)
      .then((lineageResponse) => {
        if (!cancelled) setLineageEnabled(!!lineageResponse?.namespace);
      })
      .catch(() => { if (!cancelled) setLineageEnabled(false); })
      .finally(() => { if (!cancelled) setLoadingLineage(false); });
    return () => { cancelled = true; };
  }, [lineageBaseNodeId]);

  return { lineageEnabled, loadingLineage };
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minute cache
    },
  },
});